import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './i18n'; // Initialize i18n
import App from './App';

// Auto-recover from stale chunk references after a deploy. When a user has a
// cached index.html that points at old asset hashes, dynamic imports 404 and
// Cloudflare returns the SPA fallback HTML — the browser then tries to parse
// HTML as JS and the app blanks. Force one hard reload to pick up the fresh
// index.html, and guard against an infinite loop with sessionStorage.
const CHUNK_RELOAD_KEY = '__chunk_reloaded__';
function isStaleChunkError(err) {
  const msg = String(err?.message || err || '');
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    /Expected a JavaScript-or-Wasm module script/i.test(msg)
  );
}
function recoverFromStaleChunk() {
  // Allow up to 3 attempts with backoff (a reload can land mid-deploy while new
  // assets are still propagating; one-shot recovery left users stuck on blank).
  const attempts = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0');
  if (attempts >= 3) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(attempts + 1));
  setTimeout(() => {
    // Cache-bust the document itself: a plain reload() may be served the SAME
    // stale index.html from the browser's heuristic cache (no Cache-Control
    // header pre-2026-07-17), burning every retry on identical stale HTML.
    // A changed query string forces a cache miss on the document.
    const url = new URL(window.location.href);
    url.searchParams.set('_cb', String(attempts + 1) + '-' + Math.trunc(performance.now()));
    window.location.replace(url.toString());
  }, 500 + attempts * 1500);
}
window.addEventListener('error', (e) => {
  if (isStaleChunkError(e.error || e.message)) recoverFromStaleChunk();
});
window.addEventListener('unhandledrejection', (e) => {
  if (isStaleChunkError(e.reason)) recoverFromStaleChunk();
});

// A successful mount means assets are healthy — reset the recovery budget so a
// FUTURE deploy in this same session can also self-heal.
setTimeout(() => {
  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  const url = new URL(window.location.href);
  if (url.searchParams.has('_cb')) {
    url.searchParams.delete('_cb');
    window.history.replaceState(null, '', url.toString());
  }
}, 10000);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
